import { ReactNode, useState, useMemo, useEffect, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import styles from './tableWithActions.module.scss';
import { TableSkeleton } from './TableSkeleton';

export type TableRowAction<T> = {
  label: string;
  onClick: (row: T) => void;
  condition?: (row: T) => boolean;
};

export type TableColumn<T> = {
  label: string;
  key: keyof T;
  render?: (value: T[keyof T], row: T) => ReactNode;
  sx?: Record<string, any>;
};

export type TableAction = {
  label: string;
  onClick: () => void;
  variant?: 'text' | 'outlined' | 'contained';
  color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
};

export type TableWithActionsProps<T> = {
  actions?: TableAction[];
  data: T[];
  columns: TableColumn<T>[];
  rowActions?: TableRowAction<T>[];
  hasSearch?: boolean;
  hasPagination?: boolean;
  hasActions?: boolean;
  onRowClick?: (row: T) => void;
  // Server-side pagination props
  serverSide?: boolean;
  totalCount?: number;
  currentPage?: number;
  pageSize?: number;
  onPageChange?: (page: number, pageSize: number) => void;
  // Server-side search props
  searchTerm?: string;
  onSearchChange?: (searchTerm: string) => void;
  // Loading state props
  isLoading?: boolean;
  showSkeletonRows?: boolean;
  // Empty state props
  emptyMessage?: string;
  // Refresh props
  onRefresh?: () => void;
  isRefreshing?: boolean;
};

export const TableWithActions = <T,>({
  actions,
  data,
  columns,
  rowActions,
  hasSearch = false,
  hasPagination = false,
  hasActions = false,
  onRowClick,
  serverSide = false,
  totalCount = 0,
  currentPage = 0,
  pageSize = 10,
  onPageChange,
  searchTerm: externalSearchTerm,
  onSearchChange,
  isLoading = false,
  showSkeletonRows = true,
  emptyMessage,
  onRefresh,
  isRefreshing = false,
}: TableWithActionsProps<T>) => {
  const [internalSearchTerm, setInternalSearchTerm] = useState('');
  const [displaySearchTerm, setDisplaySearchTerm] = useState(''); // For immediate visual feedback
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(''); // For actual search execution
  const [page, setPage] = useState(serverSide ? currentPage : 0);
  const [rowsPerPage, setRowsPerPage] = useState(serverSide ? pageSize : 10);

  // Ref for debounce timeout cleanup
  const debounceTimeoutRef = useRef<NodeJS.Timeout>();
  // Ref to track previous search term to prevent unnecessary onSearchChange calls
  const previousSearchTermRef = useRef<string>('');

  // Initialize display search term from external prop
  useEffect(() => {
    if (serverSide && externalSearchTerm !== undefined) {
      setDisplaySearchTerm(externalSearchTerm);
      setDebouncedSearchTerm(externalSearchTerm);
      // Initialize the previous search term ref to prevent initial unnecessary calls
      previousSearchTermRef.current = externalSearchTerm;
    }
  }, [serverSide, externalSearchTerm]);

  // For server-side pagination, sync state with props
  // Use a ref to track if we're in the middle of a user-initiated change
  const userInitiatedChangeRef = useRef(false);

  useEffect(() => {
    if (serverSide && !userInitiatedChangeRef.current) {
      setPage(currentPage);
      setRowsPerPage(pageSize);
    }
    // Reset the flag after prop changes are processed
    userInitiatedChangeRef.current = false;
  }, [serverSide, currentPage, pageSize]);

  // Debounce effect for server-side search
  useEffect(() => {
    if (!serverSide) return;

    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout for debounced search
    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(displaySearchTerm);
      // Only call onSearchChange if the search term actually changed
      if (onSearchChange && displaySearchTerm !== previousSearchTermRef.current) {
        previousSearchTermRef.current = displaySearchTerm;
        onSearchChange(displaySearchTerm);
      }
    }, 1000); // 1 second debounce delay

    // Cleanup timeout on unmount or dependency change
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [displaySearchTerm, serverSide, onSearchChange]);

  // Use external search term for server-side, internal for client-side
  const searchTerm = serverSide ? debouncedSearchTerm : internalSearchTerm;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<T | null>(null);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    // Skip client-side filtering when server-side search is enabled
    if (serverSide || !hasSearch || !searchTerm) return data;

    return data.filter((row) => {
      return columns.some((column) => {
        const value = row[column.key];
        return String(value).toLowerCase().includes(searchTerm.toLowerCase());
      });
    });
  }, [data, searchTerm, columns, hasSearch, serverSide]);

  // Paginate data (server-side vs client-side)
  const paginatedData = useMemo(() => {
    if (!hasPagination) return filteredData;

    if (serverSide) {
      // For server-side pagination, data is already paginated
      return filteredData;
    }

    // Client-side pagination
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage, hasPagination, serverSide]);

  const handleChangePage = (event: unknown, newPage: number) => {
    if (serverSide) {
      // Set flag to indicate user-initiated change
      userInitiatedChangeRef.current = true;
      // Update local state immediately for UI responsiveness
      setPage(newPage);
      // Notify parent to fetch new data
      onPageChange?.(newPage, rowsPerPage);
    } else {
      setPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    setRowsPerPage(newPageSize);

    if (serverSide) {
      onPageChange?.(0, newPageSize);
    } else {
      setPage(0);
    }
  };

  const handleRowActionClick = (event: React.MouseEvent<HTMLElement>, row: T) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleRowActionClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleRowActionSelect = (action: TableRowAction<T>) => {
    if (selectedRow) {
      action.onClick(selectedRow);
    }
    handleRowActionClose();
  };

  return (
    <div className={styles.container}>
      {/* Header with Search and Actions */}
      {(hasSearch || hasActions || onRefresh) && (
        <div className={styles.header}>
          {/* Search */}
          <div className={styles.searchContainer}>
            {hasSearch && (
              <TextField
                placeholder="Search..."
                variant="outlined"
                size="small"
                value={serverSide ? displaySearchTerm : internalSearchTerm}
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (serverSide) {
                    // For server-side search, update display term immediately for visual feedback
                    // The debounced search will be handled by the useEffect
                    setDisplaySearchTerm(newValue);
                  } else {
                    // For client-side search, update immediately (no debounce needed)
                    setInternalSearchTerm(newValue);
                  }
                }}
                className={styles.searchField}
              />
            )}
          </div>

          {/* Action Buttons */}
          <div className={styles.actionsContainer}>
            {/* Reload Button */}
            {onRefresh && (
              <IconButton
                onClick={onRefresh}
                disabled={isRefreshing}
                size="small"
                aria-label="Refresh table data"
                sx={{ mr: 1 }}
              >
                <RefreshIcon
                  sx={{
                    animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': {
                        transform: 'rotate(0deg)',
                      },
                      '100%': {
                        transform: 'rotate(360deg)',
                      },
                    },
                  }}
                />
              </IconButton>
            )}

            {/* Other Action Buttons */}
            {hasActions &&
              actions &&
              actions.map((action, index) => (
                <Button
                  key={index}
                  variant={action.variant || 'contained'}
                  color={action.color || 'primary'}
                  onClick={action.onClick}
                >
                  {action.label}
                </Button>
              ))}
          </div>
        </div>
      )}

      {/* Table */}
      <TableContainer
        className={styles.tableContainer}
        sx={(theme) => ({
          backgroundColor: theme.palette.mode === 'light' ? '#ffffff' : theme.palette.grey[900],
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
        })}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  key={index}
                  sx={(theme) => ({
                    backgroundColor:
                      theme.palette.mode === 'light'
                        ? theme.palette.grey[200]
                        : theme.palette.grey[800],
                    borderBottom: 2,
                    borderColor: 'divider',
                    fontWeight: 600,
                    ...column.sx,
                  })}
                >
                  {column.label}
                </TableCell>
              ))}
              {rowActions && rowActions.length > 0 && (
                <TableCell
                  align="right"
                  sx={(theme) => ({
                    backgroundColor:
                      theme.palette.mode === 'light'
                        ? theme.palette.grey[200]
                        : theme.palette.grey[800],
                    borderBottom: 2,
                    borderColor: 'divider',
                    fontWeight: 600,
                  })}
                ></TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && showSkeletonRows ? (
              <TableSkeleton
                columns={columns}
                rowCount={rowsPerPage}
                hasRowActions={rowActions && rowActions.length > 0}
                animate={true}
              />
            ) : paginatedData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (rowActions && rowActions.length > 0 ? 1 : 0)}
                  sx={{ border: 'none' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 8,
                      px: 2,
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ textAlign: 'center', fontSize: '1rem', whiteSpace: 'pre-line' }}
                    >
                      {emptyMessage || 'No data available'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, rowIndex) => (
                <TableRow
                  key={rowIndex}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={
                    onRowClick
                      ? { cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }
                      : undefined
                  }
                >
                  {columns.map((column, columnIndex) => (
                    <TableCell key={columnIndex} sx={column.sx}>
                      {column.render
                        ? column.render(row[column.key], row)
                        : typeof row[column.key] === 'object' && row[column.key] !== null
                          ? JSON.stringify(row[column.key])
                          : String(row[column.key] || '')}
                    </TableCell>
                  ))}
                  {(() => {
                    const availableActions = rowActions?.filter(
                      (action) => !action.condition || action.condition(row)
                    );
                    return availableActions && availableActions.length > 0 ? (
                      <TableCell align="right">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent row click when clicking action button
                            handleRowActionClick(e, row);
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    ) : rowActions && rowActions.length > 0 ? (
                      <TableCell align="right" />
                    ) : null;
                  })()}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Row Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleRowActionClose}>
        {rowActions
          ?.filter((action) => !action.condition || (selectedRow && action.condition(selectedRow)))
          .map((action, index) => (
            <MenuItem key={index} onClick={() => handleRowActionSelect(action)}>
              {action.label}
            </MenuItem>
          ))}
      </Menu>

      {/* Pagination */}
      {hasPagination && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={serverSide ? totalCount : filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          className={styles.pagination}
          sx={{
            borderTop: 0,
            borderColor: 'divider',
          }}
        />
      )}
    </div>
  );
};
